import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit"
import { ApiError, ApiResponse } from "@/api"
import {
   LogResponse,
   requestCellLogs,
   requestInmateLogs,
} from "@/api/LogsService"
import { Log } from "@/types/logs"

interface LogsState {
   loadingCellLogs: boolean
   errorCellLogs: number | null
   cellLogs: Log[]
   loadingInmateLogs: boolean
   errorInmateLogs: number | null
   inmateLogs: Log[]
}

export const initialState: LogsState = {
   loadingCellLogs: false,
   errorCellLogs: null,
   cellLogs: [],
   loadingInmateLogs: false,
   errorInmateLogs: null,
   inmateLogs: [],
}

export const fetchCellLogs = createAsyncThunk(
   "logs/fetchCellLogs",
   async (
      {
         levelId,
         timezone,
         startDate,
         endDate,
      }: {
         levelId: string
         timezone: string
         startDate?: string
         endDate?: string
      },
      thunkAPI
   ) => {
      try {
         const response: ApiResponse<LogResponse[]> = await requestCellLogs(
            levelId,
            timezone,
            startDate,
            endDate
         )
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(500)
      }
   }
)

export const fetchInmateLogs = createAsyncThunk(
   "logs/fetchInmateLogs",
   async (
      {
         inmateId,
         timezone,
         startDate,
         endDate,
      }: {
         inmateId: string
         timezone: string
         startDate?: string
         endDate?: string
      },
      thunkAPI
   ) => {
      try {
         const response: ApiResponse<LogResponse[]> = await requestInmateLogs(
            inmateId,
            timezone,
            startDate,
            endDate
         )
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(500)
      }
   }
)

const logsSlice = createSlice({
   name: "logs",
   initialState,
   reducers: {
      resetLogs: () => initialState,
      resetCellLogs: (state) => {
         state.cellLogs = []
         state.loadingCellLogs = false
         state.errorCellLogs = null
      },
      resetInmateLogs: (state) => {
         state.inmateLogs = []
         state.loadingInmateLogs = false
         state.errorInmateLogs = null
      },
   },
   extraReducers: (builder) => {
      builder
         .addCase(fetchCellLogs.pending, (state) => {
            state.loadingCellLogs = true
            state.errorCellLogs = null
         })
         .addCase(
            fetchCellLogs.fulfilled,
            (state, action: PayloadAction<ApiResponse<LogResponse[]>>) => {
               state.loadingCellLogs = false
               state.cellLogs = action.payload.data
            }
         )
         .addCase(fetchCellLogs.rejected, (state, action) => {
            state.loadingCellLogs = false
            state.errorCellLogs = action.payload as number
            state.cellLogs = []
         })
         .addCase(fetchInmateLogs.pending, (state) => {
            state.loadingInmateLogs = true
            state.errorInmateLogs = null
         })
         .addCase(
            fetchInmateLogs.fulfilled,
            (state, action: PayloadAction<ApiResponse<LogResponse[]>>) => {
               state.loadingInmateLogs = false
               state.inmateLogs = action.payload.data
            }
         )
         .addCase(fetchInmateLogs.rejected, (state, action) => {
            state.loadingInmateLogs = false
            state.errorInmateLogs = action.payload as number
            state.inmateLogs = []
         })
   },
})

export const { resetLogs, resetCellLogs, resetInmateLogs } = logsSlice.actions
export default logsSlice.reducer
